// A01 — Admin Sign-in page (sub-plan 13 §13.3 + §13.3-bis).
//
// CRITICAL — POSTs to `/v1/auth/login` (NOT supabase.auth.signInWithPassword)
// so captcha + login-by-ip / login-by-phone rate-limits + is_active gate
// apply uniformly with mobile login. On 200, calls supabase.auth.setSession({
// access_token, refresh_token }) to seed the SDK for downstream RLS reads.
//
// Captcha: token is single-use; widget is reset and local state cleared on
// EVERY response (2xx OR not).
//
// Mount-time session check: if already-authed admin lands on /login, jump
// straight to /app/dashboard.

import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  MailOutline as MailIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ENV } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { parseJwt } from "@/lib/jwt";
import { tokens } from "@findgeeks/ui-tokens";
import {
  CaptchaWidget,
  type CaptchaWidgetHandle,
} from "@/components/CaptchaWidget";

// Map server error codes from /v1/auth/login to user-readable copy. Falls
// back to the server-supplied message (or generic) when we don't translate.
function friendlyLoginError(body: { error?: string; message?: string }): string {
  switch (body.error) {
    case "invalid_credentials":
      return "Incorrect username or password.";
    case "ip_locked":
      return "Too many failed attempts from this network. Try again in a few minutes.";
    case "account_locked":
      return "This account has been temporarily locked after too many failed attempts. Try again in 15 minutes.";
    case "admin_account_locked":
      return "Admin account temporarily locked after too many failed attempts. Try again in 30 minutes.";
    case "captcha_failed":
      return "Verification check failed. Refresh the page and try again.";
    case "otp_proof_invalid_or_expired":
      return "OTP verification expired. Restart the login flow.";
    case "bad_request":
      return body.message ?? "Login id and password are required.";
    default:
      return body.message ?? body.error ?? "Login failed. Try again.";
  }
}

export function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  // Mount-time session check — skip past /login if already authed admin.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;
      const payload = parseJwt<{ app_role?: string }>(token);
      if (payload.app_role === "admin" && !cancelled) {
        navigate("/app/dashboard", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const canSubmit =
    email.trim().length > 0 &&
    password.length > 0 &&
    captchaToken !== null &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${ENV.API_BASE_URL}/v1/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken!,
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      // Discard captcha token on every response (single-use).
      captchaRef.current?.reset();
      setCaptchaToken(null);

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        setError(friendlyLoginError(body));
        return;
      }
      const json = (await res.json()) as {
        access_token: string;
        refresh_token: string;
      };
      await supabase.auth.setSession({
        access_token: json.access_token,
        refresh_token: json.refresh_token,
      });

      const payload = parseJwt<{ app_role?: string }>(json.access_token);
      if (payload.app_role !== "admin") {
        // R32/R33 — clear cache before signOut (cross-account leak).
        queryClient.clear();
        await supabase.auth.signOut();
        setError("This account is not an administrator.");
        return;
      }
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError((err as Error).message ?? "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // Background image lives at apps/admin/public/login-bg.png and is
        // served at /login-bg.png by Vite. The signInBg colour stays as a
        // fallback in case the image fails to load.
        backgroundColor: tokens.colors.admin.signInBg,
        backgroundImage: "url(/login-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: "100%",
          maxWidth: 460,
          px: 5,
          py: 4,
          borderRadius: 1,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: 26,
            fontWeight: tokens.font.weights.regular,
            mb: 3,
            color: tokens.colors.admin.textHeading,
            textAlign: "center",
          }}
        >
          Sign In
        </Typography>

        <Box component="form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Login Name"
            type="email"
            fullWidth
            required
            variant="standard"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MailIcon fontSize="small" sx={{ color: tokens.colors.admin.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            required
            variant="standard"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
            <CaptchaWidget
              ref={captchaRef}
              onToken={(t) => setCaptchaToken(t)}
              onError={() =>
                setError("Unable to verify captcha. Please try again.")
              }
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!canSubmit}
            sx={{
              py: 1.25,
              borderRadius: 1,
              textTransform: "none",
              fontSize: 15,
              backgroundColor: tokens.colors.admin.primary,
              "&:hover": { backgroundColor: tokens.colors.admin.primaryHover },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : "Sign In"}
          </Button>

          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${tokens.colors.admin.border}`,
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              gap: 1,
              fontSize: 13,
              color: tokens.colors.admin.textMuted,
            }}
          >
            <MuiLink
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              variant="body2"
              sx={{ color: tokens.colors.admin.textBody }}
            >
              Forgot Password?
            </MuiLink>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
